/**
 * The curated brand palettes, ported from LedgerFlow's ThemePalette.
 *
 * Each ramp was measured so white text on brand-600 and brand-700 text on
 * white both clear WCAG AA, which is why tenants pick from this set rather
 * than a free colour picker.
 */
export type PaletteKey =
  | 'bixo'
  | 'indigo'
  | 'violet'
  | 'blue'
  | 'sky'
  | 'teal'
  | 'emerald'
  | 'amber'
  | 'orange'
  | 'rose'
  | 'fuchsia'
  | 'slate'

export type RampStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950

export interface PaletteDefinition {
  label: string
  ramp: Record<RampStep, string>
}

export const DEFAULT_PALETTE: PaletteKey = 'bixo'

export const PALETTES: Record<PaletteKey, PaletteDefinition> = {
  // The Bixo logo blue (950 is the logo's navy). Default for new workspaces.
  bixo: {
    label: 'Bixo blue',
    ramp: {
      50: 'oklch(97% 0.016 252)',
      100: 'oklch(93.4% 0.034 253)',
      200: 'oklch(88% 0.062 254)',
      300: 'oklch(80.5% 0.108 254)',
      400: 'oklch(70.5% 0.168 256)',
      500: 'oklch(61.5% 0.215 258)',
      600: 'oklch(53.6% 0.236 260.5)',
      700: 'oklch(47.4% 0.215 262)',
      800: 'oklch(39.6% 0.17 262.5)',
      900: 'oklch(32% 0.13 262)',
      950: 'oklch(24.3% 0.1 260)',
    },
  },
  indigo: {
    label: 'Indigo',
    ramp: {
      50: 'oklch(96.2% 0.018 272.314)',
      100: 'oklch(93% 0.034 272.788)',
      200: 'oklch(87% 0.065 274.039)',
      300: 'oklch(78.5% 0.115 274.713)',
      400: 'oklch(67.3% 0.182 276.935)',
      500: 'oklch(58.5% 0.233 277.117)',
      600: 'oklch(51.1% 0.262 276.966)',
      700: 'oklch(45.7% 0.24 277.023)',
      800: 'oklch(39.8% 0.195 277.366)',
      900: 'oklch(35.9% 0.144 278.697)',
      950: 'oklch(25.7% 0.09 281.288)',
    },
  },
  violet: {
    label: 'Violet',
    ramp: {
      50: 'oklch(96.9% 0.016 293.756)',
      100: 'oklch(94.3% 0.029 294.588)',
      200: 'oklch(89.4% 0.057 293.283)',
      300: 'oklch(81.1% 0.111 293.571)',
      400: 'oklch(70.2% 0.183 293.541)',
      500: 'oklch(60.6% 0.25 292.717)',
      600: 'oklch(54.1% 0.281 293.009)',
      700: 'oklch(49.1% 0.27 292.581)',
      800: 'oklch(43.2% 0.232 292.759)',
      900: 'oklch(38% 0.189 293.745)',
      950: 'oklch(28.3% 0.141 291.089)',
    },
  },
  blue: {
    label: 'Blue',
    ramp: {
      50: 'oklch(97% 0.014 254.604)',
      100: 'oklch(93.2% 0.032 255.585)',
      200: 'oklch(88.2% 0.059 254.128)',
      300: 'oklch(80.9% 0.105 251.813)',
      400: 'oklch(70.7% 0.165 254.624)',
      500: 'oklch(62.3% 0.214 259.815)',
      600: 'oklch(54.6% 0.245 262.881)',
      700: 'oklch(48.8% 0.243 264.376)',
      800: 'oklch(42.4% 0.199 265.638)',
      900: 'oklch(37.9% 0.146 265.522)',
      950: 'oklch(28.2% 0.091 267.935)',
    },
  },
  sky: {
    label: 'Sky',
    ramp: {
      50: 'oklch(97.7% 0.013 236.62)',
      100: 'oklch(95.1% 0.026 236.824)',
      200: 'oklch(90.1% 0.058 230.902)',
      300: 'oklch(82.8% 0.111 230.318)',
      400: 'oklch(74.6% 0.16 232.661)',
      500: 'oklch(63.65% 0.1635 239.644)',
      600: 'oklch(55.28% 0.1484 242.279)',
      700: 'oklch(48.29% 0.1268 242.161)',
      800: 'oklch(43.26% 0.106 240.807)',
      900: 'oklch(38.12% 0.0876 241.104)',
      950: 'oklch(29.3% 0.066 243.157)',
    },
  },
  teal: {
    label: 'Teal',
    ramp: {
      50: 'oklch(98.4% 0.014 180.72)',
      100: 'oklch(95.3% 0.051 180.801)',
      200: 'oklch(91% 0.096 180.426)',
      300: 'oklch(85.5% 0.138 181.071)',
      400: 'oklch(77.7% 0.152 181.912)',
      500: 'oklch(60% 0.118 184.704)',
      600: 'oklch(52.88% 0.1004 186.054)',
      700: 'oklch(46.66% 0.0852 187.486)',
      800: 'oklch(41.66% 0.072 188.296)',
      900: 'oklch(36.42% 0.0596 189.238)',
      950: 'oklch(27.7% 0.046 192.524)',
    },
  },
  emerald: {
    label: 'Emerald',
    ramp: {
      50: 'oklch(97.9% 0.021 166.113)',
      100: 'oklch(95% 0.052 163.051)',
      200: 'oklch(90.5% 0.093 164.15)',
      300: 'oklch(84.5% 0.143 164.978)',
      400: 'oklch(76.5% 0.177 163.223)',
      500: 'oklch(59.6% 0.145 163.225)',
      600: 'oklch(52.56% 0.1234 165.135)',
      700: 'oklch(46.24% 0.1042 166.393)',
      800: 'oklch(41.04% 0.0878 167.724)',
      900: 'oklch(35.48% 0.0718 169.662)',
      950: 'oklch(26.2% 0.051 172.552)',
    },
  },
  amber: {
    label: 'Amber',
    ramp: {
      50: 'oklch(98.7% 0.022 95.277)',
      100: 'oklch(96.2% 0.059 95.617)',
      200: 'oklch(92.4% 0.12 95.746)',
      300: 'oklch(87.9% 0.169 91.605)',
      400: 'oklch(82.8% 0.189 84.429)',
      500: 'oklch(66.6% 0.179 58.318)',
      600: 'oklch(57.72% 0.1662 50.862)',
      700: 'oklch(50.58% 0.1474 47.32)',
      800: 'oklch(44.94% 0.127 46.082)',
      900: 'oklch(38.7% 0.105 45.85)',
      950: 'oklch(27.9% 0.077 45.635)',
    },
  },
  orange: {
    label: 'Orange',
    ramp: {
      50: 'oklch(98% 0.016 73.684)',
      100: 'oklch(95.4% 0.038 75.164)',
      200: 'oklch(90.1% 0.076 70.697)',
      300: 'oklch(83.7% 0.128 66.29)',
      400: 'oklch(75% 0.183 55.934)',
      500: 'oklch(64.6% 0.222 41.116)',
      600: 'oklch(57.16% 0.2004 38.945)',
      700: 'oklch(50.32% 0.1722 37.743)',
      800: 'oklch(44.52% 0.1434 37.651)',
      900: 'oklch(37.96% 0.1142 37.789)',
      950: 'oklch(26.6% 0.079 36.259)',
    },
  },
  rose: {
    label: 'Rose',
    ramp: {
      50: 'oklch(96.9% 0.015 12.422)',
      100: 'oklch(94.1% 0.03 12.58)',
      200: 'oklch(89.2% 0.058 10.001)',
      300: 'oklch(81% 0.117 11.638)',
      400: 'oklch(71.2% 0.194 13.428)',
      500: 'oklch(64.5% 0.246 16.439)',
      600: 'oklch(58.6% 0.253 17.585)',
      700: 'oklch(51.4% 0.222 16.935)',
      800: 'oklch(45.5% 0.188 13.697)',
      900: 'oklch(41% 0.159 10.272)',
      950: 'oklch(27.1% 0.105 12.094)',
    },
  },
  fuchsia: {
    label: 'Fuchsia',
    ramp: {
      50: 'oklch(97.7% 0.017 320.058)',
      100: 'oklch(95.2% 0.037 318.852)',
      200: 'oklch(90.3% 0.076 319.62)',
      300: 'oklch(83.3% 0.145 321.434)',
      400: 'oklch(74% 0.238 322.16)',
      500: 'oklch(66.7% 0.295 322.15)',
      600: 'oklch(59.1% 0.293 322.896)',
      700: 'oklch(51.8% 0.253 323.949)',
      800: 'oklch(45.2% 0.211 324.591)',
      900: 'oklch(40.1% 0.17 325.612)',
      950: 'oklch(29.3% 0.136 325.661)',
    },
  },
  slate: {
    label: 'Graphite',
    ramp: {
      50: 'oklch(98.4% 0.003 247.858)',
      100: 'oklch(96.8% 0.007 247.896)',
      200: 'oklch(92.9% 0.013 255.508)',
      300: 'oklch(86.9% 0.022 252.894)',
      400: 'oklch(70.4% 0.04 256.788)',
      500: 'oklch(55.4% 0.046 257.417)',
      600: 'oklch(44.6% 0.043 257.281)',
      700: 'oklch(37.2% 0.044 257.287)',
      800: 'oklch(27.9% 0.041 260.031)',
      900: 'oklch(20.8% 0.042 265.755)',
      950: 'oklch(12.9% 0.042 264.695)',
    },
  },
}

export const PALETTE_KEYS = Object.keys(PALETTES) as PaletteKey[]

export function isPaletteKey(value: unknown): value is PaletteKey {
  return typeof value === 'string' && value in PALETTES
}

/** Point every brand-* utility at the chosen palette, without a rebuild. */
export function applyPalette(
  key: string | null | undefined,
  target: HTMLElement = document.documentElement,
): void {
  const palette = PALETTES[isPaletteKey(key) ? key : DEFAULT_PALETTE]

  for (const [step, value] of Object.entries(palette.ramp)) {
    target.style.setProperty(`--color-brand-${step}`, value)
  }

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = palette.ramp[600]
}
